/**
 * createAgoraClient — thin factory wrapping `AgoraRTC.createClient(...)`.
 *
 * Default behaviour is identical to the inline construction this replaced
 * in `useRoomAgora.ts` — same dynamic `import('agora-rtc-sdk-ng')`, the
 * config is the caller's (`useRoomAgora` passes `{ mode: 'live', codec: 'vp8' }`
 * since CORE-1430).
 *
 * The wrapping exists so an e2e mock can substitute the RTC client without
 * touching the hook itself: when `window.__amb_agora_factory__` is a
 * function, that function is called instead of building a real Agora
 * client. The substitute returns an object with the same surface area the
 * hook uses (`on`, `subscribe`, `join`, `leave`, `publish`, `renewToken`,
 * `enableAudioVolumeIndicator`, `remoteUsers`).
 *
 * AMB-233 slice 2 — backs AMB-208 #1 (mic/cam icons via remoteUsers).
 */
import type { IAgoraRTCClient, ClientConfig } from 'agora-rtc-sdk-ng';
import { safeImport } from '../utils/staleBundle';

/**
 * Subset of the Agora RTC client that `useRoomAgora` actually touches —
 * declared as a structural type so a mock doesn't have to satisfy the
 * full 200+ method surface of `IAgoraRTCClient`. The hook never reads
 * arbitrary fields, only the ones below.
 */
export type AgoraClientLike = Pick<
    IAgoraRTCClient,
    'on' | 'subscribe' | 'join' | 'leave' | 'publish' | 'renewToken' | 'enableAudioVolumeIndicator'
> & {
    /** Read-only on the SDK; readonly here too so callers can't shadow it. */
    readonly remoteUsers: IAgoraRTCClient['remoteUsers'];
    /**
     * CORE-1202/1193 — what the SDK says it is actually sending. The one fact
     * that separates «камера включена» from «видео идёт на провод»; every
     * reader treats a missing field as «nothing published» via `?? []`, which
     * is also what the e2e fake client (no publishing at all) means by it.
     */
    readonly localTracks?: IAgoraRTCClient['localTracks'];
    /**
     * CORE-1292 — releases a subscription the client believes in but does not
     * hold, so the re-subscribe that follows can't collide with a half-open one.
     * Optional on purpose: the e2e fake client doesn't implement it, and there
     * is nothing to release there either.
     */
    unsubscribe?: IAgoraRTCClient['unsubscribe'];
    /**
     * CORE-1430 — роль в режиме `live` (host / audience). Необязательна: фейковый
     * клиент e2e её не реализует, и роль ему не нужна.
     */
    setClientRole?: IAgoraRTCClient['setClientRole'];
    /**
     * CORE-1917 — dual stream: the publisher sends a full and a small stream, the receiver
     * picks per container and lets the SDK fall back on a poor downlink. All optional: the
     * e2e fake client has none of them, and every call is guarded with `?.`.
     */
    enableDualStream?: IAgoraRTCClient['enableDualStream'];
    /** CORE-2034 — off for the length of a downshift, on again with the restore. */
    disableDualStream?: IAgoraRTCClient['disableDualStream'];
    setLowStreamParameter?: IAgoraRTCClient['setLowStreamParameter'];
    setRemoteVideoStreamType?: IAgoraRTCClient['setRemoteVideoStreamType'];
    setStreamFallbackOption?: IAgoraRTCClient['setStreamFallbackOption'];
};

/**
 * Create (or fetch a mocked) Agora RTC client. Returns a Promise because the
 * default path dynamic-imports the SDK — keeps it out of the main web
 * bundle for non-room pages.
 */
export async function createAgoraClient(config: ClientConfig): Promise<AgoraClientLike> {
    // E2E substitution path. Playwright's `addInitScript` plants this
    // factory before the bundle boots; in production the global is absent.
    if (typeof window !== 'undefined') {
        const factory = (window as any).__amb_agora_factory__;
        if (typeof factory === 'function') {
            try {
                const fake = await factory(config);
                if (fake && typeof fake.on === 'function' && typeof fake.join === 'function') {
                    return fake as AgoraClientLike;
                }
                // Bad return — fall through to the real SDK so the test
                // fails noisily instead of silently sticking.
            } catch {
                /* fall through */
            }
        }
    }

    const AgoraRTC = (await safeImport(() => import('agora-rtc-sdk-ng'))).default;
    applySignalingStallParameters(AgoraRTC);
    pinAgoraWebAudioSampleRate(AgoraRTC);
    return AgoraRTC.createClient(config) as AgoraClientLike;
}

/**
 * CORE-1410 — how many unanswered signaling pings (one every 3 s) the SDK
 * tolerates before it declares RECONNECTING. The SDK default is 10, and a
 * signaling socket that silently stops delivering frames goes unnoticed for
 * ~30 s: no `user-published`, no mute changes, while the room looks connected.
 * The SDK's own reconnect then restores the event flow. Measured with a
 * simulated stall (frames dropped server→page, real SDK 4.24.2, dev channel):
 * default 10 → RECONNECTING 28.4 s after the stall; 5 → 13.5 s. The SDK still
 * also requires 10 s without any socket message (`WEBSOCKET_TIMEOUT_MIN`), so a
 * slow-but-alive link is not torn down by this.
 */
export const AGORA_PING_PONG_TIME_OUT = 5;

/**
 * `setParameter` is a real export of the SDK but absent from its typings, and
 * the value is SDK-global. Best effort: a build without it keeps the default.
 */
export function applySignalingStallParameters(AgoraRTC: unknown): void {
    const setParameter = (AgoraRTC as { setParameter?: (key: string, value: unknown) => void }).setParameter;
    if (typeof setParameter !== 'function') return;
    try {
        setParameter.call(AgoraRTC, 'PING_PONG_TIME_OUT', AGORA_PING_PONG_TIME_OUT);
    } catch {
        /* unknown parameter on this SDK build — keep its default */
    }
}

/**
 * CORE-844 — Agora routes every local and remote audio track through ONE
 * page-wide AudioContext, built lazily on first use with
 * `new AudioContext(WEBAUDIO_INIT_OPTIONS)`. The SDK default is no options, so
 * the context takes the rate of the current OUTPUT device. On dev that was
 * 24000 Hz on the ticket author's machine (and a few others, 10.08–23.09)
 * while every mic track is 48000 Hz — the only 2:1 ratio in the telemetry, and
 * 2:1 is exactly the "minion voice" (pitch and speed doubled). Pinning the
 * context to 48 kHz makes the graph rate independent of whatever headset or
 * AV device is plugged in; the browser resamples to the output device itself.
 *
 * Must run before the first Agora audio track exists: once the context is
 * built the option is never re-read. Best effort, like the one above.
 */
export const AGORA_WEBAUDIO_SAMPLE_RATE = 48000;

export function pinAgoraWebAudioSampleRate(AgoraRTC: unknown): void {
    const setParameter = (AgoraRTC as { setParameter?: (key: string, value: unknown) => void }).setParameter;
    if (typeof setParameter !== 'function') return;
    try {
        setParameter.call(AgoraRTC, 'WEBAUDIO_INIT_OPTIONS', { sampleRate: AGORA_WEBAUDIO_SAMPLE_RATE });
    } catch {
        /* unknown parameter on this SDK build — keep its default */
    }
}
